'use client';

import { useState, useMemo } from 'react';
import { X, ArrowLeftRight } from 'lucide-react';

interface Props {
  productModel: any;
  currentSelections: Record<string, unknown>;
  resolveSelectionLabel: (key: string, value: any) => { label: string; display: string };
  onClose: () => void;
}

export function ComparisonMode({ productModel, currentSelections, resolveSelectionLabel, onClose }: Props) {
  const [savedSnapshots, setSavedSnapshots] = useState<Array<{ id: number; name: string; selections: Record<string, unknown> }>>([]);
  const [snapName, setSnapName] = useState('');

  function handleSaveSnapshot() {
    const name = snapName.trim() || `Snapshot ${savedSnapshots.length + 1}`;
    setSavedSnapshots((prev) => [...prev, { id: Date.now(), name, selections: { ...currentSelections } }]);
    setSnapName('');
  }

  const [compareA, setCompareA] = useState<number | null>(null);
  const [compareB, setCompareB] = useState<number | null>(null);

  const snapA = savedSnapshots.find((s) => s.id === compareA);
  const snapB = savedSnapshots.find((s) => s.id === compareB);

  const diff = useMemo(() => {
    if (!snapA || !snapB) return [];
    const allKeys = new Set([...Object.keys(snapA.selections), ...Object.keys(snapB.selections)]);
    return Array.from(allKeys).map((key) => {
      const valA = snapA.selections[key];
      const valB = snapB.selections[key];
      const labelA = valA ? resolveSelectionLabel(key, valA) : null;
      const labelB = valB ? resolveSelectionLabel(key, valB) : null;
      return {
        key,
        label: labelA?.label || labelB?.label || key,
        a: labelA?.display || '—',
        b: labelB?.display || '—',
        changed: valA !== valB,
      };
    });
  }, [snapA, snapB, resolveSelectionLabel]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4 text-blue-500" />
            <h3 className="text-sm font-semibold">Configuration Comparison</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center gap-2">
            <input
              value={snapName}
              onChange={(e) => setSnapName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveSnapshot()}
              placeholder="Snapshot name (optional)"
              className="flex-1 h-8 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-3 text-xs"
            />
            <button
              onClick={handleSaveSnapshot}
              className="h-8 px-3 text-xs font-medium rounded-md bg-blue-500 text-white hover:bg-blue-600 transition-colors"
            >
              Save Current
            </button>
          </div>
          {savedSnapshots.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {savedSnapshots.map((snap) => (
                <span
                  key={snap.id}
                  className="text-[10px] bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 px-2 py-0.5 rounded-full"
                >
                  {snap.name}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {savedSnapshots.length < 2 ? (
            <div className="text-center py-12">
              <ArrowLeftRight className="h-8 w-8 text-zinc-300 dark:text-zinc-700 mx-auto mb-3" />
              <p className="text-sm text-zinc-500">Save at least 2 snapshots to compare</p>
              <p className="text-xs text-zinc-400 mt-1">
                Configure your product, save a snapshot, change options, save another, then compare.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-medium text-zinc-500 mb-1 block">Snapshot A</label>
                  <select
                    value={compareA ?? ''}
                    onChange={(e) => setCompareA(Number(e.target.value) || null)}
                    className="w-full h-8 text-xs rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-2"
                  >
                    <option value="">Select...</option>
                    {savedSnapshots.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-zinc-500 mb-1 block">Snapshot B</label>
                  <select
                    value={compareB ?? ''}
                    onChange={(e) => setCompareB(Number(e.target.value) || null)}
                    className="w-full h-8 text-xs rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-2"
                  >
                    <option value="">Select...</option>
                    {savedSnapshots.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {snapA && snapB && (
                <div className="border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-3 text-[10px] font-medium text-zinc-500 bg-zinc-50 dark:bg-zinc-900 px-3 py-2 border-b border-zinc-200 dark:border-zinc-800">
                    <span>Attribute</span>
                    <span className="text-center">{snapA.name}</span>
                    <span className="text-center">{snapB.name}</span>
                  </div>
                  {diff.map((row) => (
                    <div
                      key={row.key}
                      className={`grid grid-cols-3 text-xs px-3 py-2 border-b border-zinc-100 dark:border-zinc-800 last:border-b-0 ${
                        row.changed
                          ? 'bg-amber-50 dark:bg-amber-950/10'
                          : ''
                      }`}
                    >
                      <span className="font-medium text-zinc-600 dark:text-zinc-400">{row.label}</span>
                      <span className={`text-center ${row.changed ? 'text-red-500 dark:text-red-400 font-medium' : 'text-zinc-500'}`}>
                        {row.a}
                      </span>
                      <span className={`text-center ${row.changed ? 'text-green-600 dark:text-green-400 font-medium' : 'text-zinc-500'}`}>
                        {row.b}
                      </span>
                    </div>
                  ))}
                  <div className="px-3 py-2 bg-zinc-50 dark:bg-zinc-900 text-[10px] text-zinc-400">
                    {diff.filter((d) => d.changed).length} difference(s) found
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
